import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import ReCAPTCHA from 'react-google-recaptcha';
import { 
    Briefcase, Shield, Users, Award, ChevronRight, CheckCircle2,
    Phone, Mail, MapPin, Target, Clock, Upload, X
} from 'lucide-react';

const careerBenefits = [
    { icon: Shield, title: 'Job Security', description: 'Stable employment with a reputed organization' },
    { icon: Award, title: 'Training & Growth', description: 'Continuous skill development programs' },
    { icon: Users, title: 'Team Environment', description: 'Work with disciplined professionals' },
    { icon: Target, title: 'Career Progression', description: 'Clear path from guard to supervisor' },
];

const openPositions = [
    {
        title: 'Security Guards',
        type: 'Full-time',
        locations: ['Bangalore'],
        description: 'Join our team of professional security guards deployed across corporate offices, malls, hospitals, and residential complexes. We seek disciplined individuals committed to maintaining safety and security.',
        requirements: [
            'Minimum 10th pass education',
            'Age between 21-45 years',
            'Physically fit with good health',
            'No criminal background',
            'Ex-servicemen preferred',
        ],
        benefits: [
            'Competitive salary',
            'PF & ESI benefits',
            'Uniforms provided',
            'Regular training',
        ],
    },
    {
        title: 'Housekeeping Staff',
        type: 'Full-time',
        locations: ['Bangalore'],
        description: 'Join our housekeeping team to maintain clean, hygienic, and well-managed premises across offices, IT companies, malls, and hospitals. Ideal for reliable, service-oriented individuals.',
        requirements: [
            'Minimum 8th pass education',
            'Physically fit and active',
            'Attention to cleanliness and hygiene',
            'Basic communication skills',
            'Prior housekeeping experience preferred',
        ],
        benefits: [
            'Stable employment',
            'PF & ESI benefits',
            'Uniforms provided',
            'Supportive team environment',
        ],
    },
    {
        title: 'Supervisors',
        type: 'Full-time',
        locations: ['Bangalore'],
        description: 'Lead and manage security teams across multiple sites. Supervisors are responsible for team coordination, client communication, and ensuring service quality standards are maintained.',
        requirements: [
            'Minimum 12th pass / Graduate',
            'Prior security experience (3+ years)',
            'Leadership and communication skills',
            'Computer literacy preferred',
            'Ex-JCOs / NCOs preferred',
        ],
        benefits: [
            'Higher salary package',
            'PF & ESI benefits',
            'Career advancement opportunities',
            'Performance incentives',
        ],
    },
    {
        title: 'Operations & Admin',
        type: 'Full-time',
        locations: ['Bangalore'],
        description: 'Support our operations through administrative roles including scheduling, payroll coordination, client documentation, and office management. Ideal for detail-oriented professionals.',
        requirements: [
            'Graduate in any discipline',
            'Proficient in MS Office',
            'Good communication skills',
            'Organizational abilities',
            '1-2 years office experience',
        ],
        benefits: [
            'Office-based role',
            'Fixed working hours',
            'PF & ESI benefits',
            'Professional development',
        ],
    },
];

export const CareersPage = () => {
    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || (process.env.NODE_ENV === 'development' ? 'http://127.0.0.1:8000' : '');
    const [isApplyOpen, setIsApplyOpen] = useState(false);
    const [selectedRole, setSelectedRole] = useState('');
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [recaptchaToken, setRecaptchaToken] = useState('');
    const recaptchaRef = useRef(null);
    const allowCloseRef = useRef(false);
    const [pendingRole, setPendingRole] = useState('');
    const [showRoleConfirm, setShowRoleConfirm] = useState(false);
    const [formData, setFormData] = useState({
        fullName: '',
        email: '',
        phone: '',
        location: '',
        experience: '',
        message: '',
    });
    const autoCloseRef = useRef(null);
    const normalizedRole = selectedRole.toLowerCase();
    const resumeRequired = normalizedRole.includes('supervisor') || (normalizedRole.includes('operations') && normalizedRole.includes('admin'));
    const successCopy = (() => {
        if (normalizedRole.includes('security guard')) {
            return {
                title: 'Security Guard Application Received',
                body: 'We received your application. Our deployment team will review your details and contact you within 24 to 48 hours.',
            };
        }
        if (normalizedRole.includes('housekeeping')) {
            return {
                title: 'Housekeeping Application Received',
                body: 'We received your application. We will review your availability and location and contact you within 24 to 48 hours.',
            };
        }
        if (normalizedRole.includes('supervisor')) {
            return {
                title: 'Supervisor Application Received',
                body: 'We received your application. Our team will review your leadership experience and get back to you within 24 to 48 hours.',
            };
        }
        if (resumeRequired) {
            return {
                title: 'Operations & Admin Application Received',
                body: 'We received your application and resume. HR will review your details and contact you within 24 to 48 hours.',
            };
        }
        return {
            title: 'Application Received',
            body: 'We received your application and will revert back to you within 24 to 48 hours.',
        };
    })();

    const isDirty = Object.values(formData).some((value) => value && value.toString().trim() !== '');

    const handleApply = (role) => {
        if (isDirty && selectedRole && selectedRole !== role) {
            setPendingRole(role);
            setShowRoleConfirm(true);
            return;
        }

        if (!selectedRole) {
            setSelectedRole(role);
        } else if (selectedRole !== role) {
            setSelectedRole(role);
        }
        setIsSubmitted(false);
        setIsApplyOpen(true);
    };

    const handleOpenChange = (open) => {
        if (open) {
            setIsApplyOpen(true);
            return;
        }
        if (!allowCloseRef.current) {
            return;
        }
        allowCloseRef.current = false;
        setIsApplyOpen(false);
        if (!open) {
            setIsSubmitted(false);
            setIsSubmitting(false);
            setRecaptchaToken('');
            recaptchaRef.current?.reset();
        }
    };

    const requestClose = () => {
        allowCloseRef.current = true;
        setIsApplyOpen(false);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!recaptchaToken) {
            toast.error('Please complete the captcha verification.');
            return;
        }
        const phoneDigits = formData.phone.replace(/\D/g, '');
        if (!/^[6-9]\d{9}$/.test(phoneDigits)) {
            toast.error('Please enter a valid 10-digit Indian mobile number.');
            return;
        }
        if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            toast.error('Please enter a valid email address.');
            return;
        }

        const formPayload = new FormData(event.currentTarget);
        formPayload.set('position', selectedRole);
        formPayload.append('recaptcha_token', recaptchaToken);
        const resumeFile = formPayload.get('resume');
        if (!resumeRequired && resumeFile instanceof File && resumeFile.size === 0) {
            formPayload.delete('resume');
        }

        setIsSubmitting(true);
        try {
            const response = await fetch(`${apiBaseUrl}/api/careers`, {
                method: 'POST',
                body: formPayload,
            });
            const result = await response.json();
            if (!response.ok || !result.success) {
                throw new Error(result.error || 'Failed to submit application');
            }
            setIsSubmitted(true);
            recaptchaRef.current?.reset();
            setRecaptchaToken('');
            setFormData({
                fullName: '',
                email: '',
                phone: '',
                location: '',
                experience: '',
                message: '',
            });
            if (autoCloseRef.current) {
                clearTimeout(autoCloseRef.current);
            }
            autoCloseRef.current = setTimeout(() => {
                requestClose();
            }, 4000);
        } catch (err) {
            const message = err?.message?.toLowerCase?.().includes('timeout')
                ? 'Captcha verification timed out or was blocked. Please refresh the page and try again.'
                : 'Unable to submit your application. Please try again later.';
            toast.error(message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleFieldChange = (event) => {
        const { name, value } = event.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleConfirmRoleChange = () => {
        setShowRoleConfirm(false);
        setSelectedRole(pendingRole);
        setPendingRole('');
        setFormData({
            fullName: '',
            email: '',
            phone: '',
            location: '',
            experience: '',
            message: '',
        });
        recaptchaRef.current?.reset();
        setRecaptchaToken('');
        setIsSubmitted(false);
        setIsApplyOpen(true);
    };

    const handleCancelRoleChange = () => {
        setShowRoleConfirm(false);
        setPendingRole('');
        setIsApplyOpen(true);
    };

    useEffect(() => {
        return () => {
            if (autoCloseRef.current) {
                clearTimeout(autoCloseRef.current);
            }
        };
    }, []);

    return (
        <div className="overflow-hidden">
            {/* Hero Section */}
            <section className="relative min-h-[50vh] lg:min-h-[60vh] flex items-center pt-24 pb-12">
                {/* Background */}
                <div className="absolute inset-0 bg-muted/50" />
                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5" />
                {/* Watermark Image - Hero Only */}
                <div 
                    className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                    style={{ 
                        backgroundImage: 'url(/images/about-1.png)',
                        filter: 'saturate(0.3) brightness(1.1)',
                        opacity: '0.1'
                    }}
                />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-background" />
                
                <div className="container mx-auto px-4 lg:px-8 relative z-10">
                    <div className="max-w-3xl">
                        <Badge className="bg-accent/10 text-accent border-accent/20 mb-6 animate-fade-in">
                            <Briefcase className="w-3 h-3 mr-1" />
                            CAREERS
                        </Badge>
                        <h1 className="font-display text-5xl lg:text-7xl text-foreground leading-tight mb-6 animate-fade-in animation-delay-100">
                            JOIN OUR
                            <span className="text-accent block">TEAM</span>
                        </h1>
                        <p className="text-muted-foreground text-lg lg:text-xl leading-relaxed animate-fade-in animation-delay-200">
                            Build a rewarding career with Shiva Security Services. We value discipline, 
                            integrity, and dedication — qualities that define our team of professionals.
                        </p>
                    </div>
                </div>
                
                {/* Horizontal Divider */}
                <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border to-transparent" />
            </section>

            {/* Why Join Us Section */}
            <section className="py-20 lg:py-32 px-4 lg:px-8">
                <div className="container mx-auto">
                    <div className="text-center max-w-3xl mx-auto mb-16">
                        <Badge className="bg-accent/10 text-accent border-accent/20 mb-4">
                            WHY JOIN US
                        </Badge>
                        <h2 className="font-display text-4xl lg:text-5xl text-foreground mb-6">
                            A CAREER WITH <span className="text-gradient-gold">PURPOSE</span>
                        </h2>
                        <p className="text-muted-foreground text-lg">
                            Since 2007, we have built a reputation for excellence. Join an organization 
                            that values your growth and provides long-term career opportunities.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
                        {careerBenefits.map((benefit, index) => (
                            <Card 
                                key={index}
                                className="card-elevated p-6 text-center hover:border-accent/30"
                            >
                                <div className="w-14 h-14 rounded-xl bg-accent/10 flex items-center justify-center mx-auto mb-4">
                                    <benefit.icon className="w-7 h-7 text-accent" />
                                </div>
                                <h3 className="font-display text-xl text-foreground mb-2">{benefit.title}</h3>
                                <p className="text-muted-foreground text-sm">{benefit.description}</p>
                            </Card>
                        ))}
                    </div>
                </div>
            </section>

            {/* Open Positions Section */}
            <section className="py-20 lg:py-32 bg-muted/30 px-4 lg:px-8">
                <div className="container mx-auto">
                    <div className="text-center max-w-3xl mx-auto mb-16">
                        <Badge className="bg-accent/10 text-accent border-accent/20 mb-4">
                            OPEN POSITIONS
                        </Badge>
                        <h2 className="font-display text-4xl lg:text-5xl text-foreground mb-6">
                            CURRENT OPPORTUNITIES
                        </h2>
                        <p className="text-muted-foreground text-lg">
                            Explore our current openings and find a role that matches your skills and aspirations.
                        </p>
                    </div>

                    <div className="space-y-8">
                        {openPositions.map((position, index) => (
                            <Card 
                                key={index}
                                className="card-elevated overflow-hidden"
                            >
                                <div className="p-6 lg:p-8">
                                    <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-6">
                                        <div>
                                            <div className="flex items-center gap-3 mb-2">
                                                <h3 className="font-display text-2xl lg:text-3xl text-foreground">
                                                    {position.title}
                                                </h3>
                                                <Badge className="bg-accent text-accent-foreground">
                                                    {position.type}
                                                </Badge>
                                            </div>
                                            <div className="flex items-center gap-2 text-muted-foreground text-sm">
                                                <MapPin className="w-4 h-4" />
                                                {position.locations.join(' • ')}
                                            </div>
                                        </div>
                                        <div className="flex items-center">
                                            <Button
                                                className="btn-accent rounded-lg px-5"
                                                onClick={() => handleApply(position.title)}
                                            >
                                                Apply Now
                                                <ChevronRight className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    </div>
                                    
                                    <p className="text-muted-foreground mb-6 leading-relaxed">
                                        {position.description}
                                    </p>

                                    <div className="grid lg:grid-cols-2 gap-6">
                                        <div>
                                            <h4 className="font-semibold text-foreground mb-3">Requirements</h4>
                                            <ul className="space-y-2">
                                                {position.requirements.map((req, i) => (
                                                    <li key={i} className="flex items-start gap-2 text-muted-foreground text-sm">
                                                        <CheckCircle2 className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                                                        {req}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-foreground mb-3">Benefits</h4>
                                            <ul className="space-y-2">
                                                {position.benefits.map((benefit, i) => (
                                                    <li key={i} className="flex items-start gap-2 text-muted-foreground text-sm">
                                                        <CheckCircle2 className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                                                        {benefit}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </Card>
                        ))}
                    </div>
                </div>
            </section>

            {/* How to Apply Section */}
            <section className="py-20 lg:py-32 px-4 lg:px-8">
                <div className="container mx-auto">
                    <Card className="bg-hero-pattern p-8 lg:p-16">
                        <div className="max-w-3xl mx-auto text-center">
                            <h2 className="font-display text-4xl lg:text-5xl text-primary-foreground mb-6">
                                HOW TO APPLY
                            </h2>
                            <p className="text-primary-foreground/80 text-lg mb-8">
                                Interested in joining our team? Contact us directly with your resume 
                                and we will get back to you within 48 hours.
                            </p>
                            
                            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-8">
                                <a href="tel:+919606924394" className="flex items-center gap-2 text-primary-foreground hover:text-accent transition-colors">
                                    <Phone className="w-5 h-5" />
                                    <span className="font-semibold">+91-9606924394</span>
                                </a>
                                <a href="mailto:enquiry@shivasecurity.com" className="flex items-center gap-2 text-primary-foreground hover:text-accent transition-colors">
                                    <Mail className="w-5 h-5" />
                                    <span className="font-semibold">enquiry@shivasecurity.com</span>
                                </a>
                            </div>

                            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                <Link to="/contact">
                                    <Button className="btn-accent rounded-lg px-8 py-6 text-lg font-semibold">
                                        Contact Us
                                        <ChevronRight className="w-5 h-5 ml-2" />
                                    </Button>
                                </Link>
                            </div>
                        </div>
                    </Card>
                </div>
            </section>

            <Dialog open={isApplyOpen} onOpenChange={handleOpenChange} modal={false}>
                <DialogContent className="max-w-2xl w-[95vw] sm:w-full max-h-[90vh] p-0 overflow-hidden flex flex-col" showClose={false} hideOverlay>
                    <div className="bg-gradient-to-br from-accent/15 via-background to-background p-4 sm:p-6 lg:p-8 border-b border-border">
                        <DialogHeader className="text-left antialiased relative">
                            <DialogTitle className="text-xl sm:text-2xl lg:text-3xl font-sans font-semibold text-foreground tracking-wide uppercase leading-snug">
                                {isSubmitted ? (
                                    <span className="animate-soft-pulse font-extrabold">
                                        <CheckCircle2 className="h-6 w-6 text-accent" strokeWidth={3} />
                                        Application Submitted Successfully
                                    </span>
                                ) : (
                                    `Apply for ${selectedRole || 'a role'}`
                                )}
                            </DialogTitle>
                            <DialogDescription className="text-sm sm:text-base text-muted-foreground">
                                {isSubmitted
                                    ? successCopy.body
                                    : 'Share your details and upload your resume. Resume is required only for Supervisors and Operations & Admin roles.'}
                            </DialogDescription>
                            <button
                                type="button"
                                onClick={requestClose}
                                className="absolute right-0 top-0 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                                aria-label="Close"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </DialogHeader>
                        <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-accent/30 bg-accent/10 px-3 py-1.5 text-xs sm:text-sm text-accent">
                            <Target className="w-4 h-4" />
                            Priority review within 24 to 48 hours
                        </div>
                    </div>

                    <div className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
                        {isSubmitted ? (
                            <div className="rounded-2xl border border-accent/20 bg-accent/10 p-6 text-center">
                                <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-accent text-accent-foreground">
                                    <CheckCircle2 className="h-6 w-6" />
                                </div>
                                <h3 className="text-xl font-semibold text-foreground mb-2">{successCopy.title}</h3>
                                <p className="text-muted-foreground mb-6">{successCopy.body}</p>
                                <Button className="btn-accent" onClick={() => setIsApplyOpen(false)}>
                                    Close
                                </Button>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} className="space-y-6">
                                <div className="grid gap-4 md:grid-cols-2">
                                    <div className="space-y-2">
                                        <Label htmlFor="fullName">Full Name</Label>
                                        <Input
                                            id="fullName"
                                            name="fullName"
                                            placeholder="Your full name"
                                            value={formData.fullName}
                                            onChange={handleFieldChange}
                                            required
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="email">Email Address</Label>
                                        <Input
                                            id="email"
                                            name="email"
                                            type="email"
                                            placeholder="you@example.com"
                                            value={formData.email}
                                            onChange={handleFieldChange}
                                            required
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="phone">Phone Number</Label>
                                        <div className="flex items-center rounded-md border border-border bg-card focus-within:border-accent">
                                            <span className="flex items-center gap-2 px-3 text-sm text-foreground">
                                                <span className="text-base" aria-hidden="true">🇮🇳</span>
                                                +91
                                            </span>
                                            <Input
                                                id="phone"
                                                name="phone"
                                                type="tel"
                                                placeholder="9876543210"
                                                value={formData.phone}
                                                onChange={handleFieldChange}
                                                inputMode="numeric"
                                                pattern="[6-9][0-9]{9}"
                                                maxLength={10}
                                                className="border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0"
                                                required
                                            />
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="location">Current Location</Label>
                                        <Input
                                            id="location"
                                            name="location"
                                            placeholder="City"
                                            value={formData.location}
                                            onChange={handleFieldChange}
                                            required
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="experience">Experience (Years)</Label>
                                        <Input
                                            id="experience"
                                            name="experience"
                                            type="number"
                                            min="0"
                                            placeholder="0"
                                            value={formData.experience}
                                            onChange={handleFieldChange}
                                            required
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="position">Position Applied For</Label>
                                        <Input id="position" name="position" value={selectedRole} readOnly className="bg-muted/40" />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="message">Tell us about yourself</Label>
                                    <Textarea
                                        id="message"
                                        name="message"
                                        rows={4}
                                        placeholder="Share a brief note about your experience and availability."
                                        value={formData.message}
                                        onChange={handleFieldChange}
                                    />
                                </div>

                                <div className="grid gap-6 md:grid-cols-2">
                                    <div className="space-y-2">
                                        <Label htmlFor="resume">
                                            Upload Resume {resumeRequired ? <span className="text-destructive">*</span> : '(Optional)'}
                                        </Label>
                                        <div className="flex items-center gap-3 rounded-md border border-dashed border-border p-4">
                                            <Upload className="h-5 w-5 text-muted-foreground" />
                                            <Input
                                                id="resume"
                                                name="resume"
                                                type="file"
                                                accept=".pdf,.doc,.docx"
                                                required={resumeRequired}
                                                className="w-full"
                                            />
                                        </div>
                                        <p className="text-xs text-muted-foreground">
                                            Accepted formats: PDF, DOC, DOCX. Max 5MB. Resume required for Supervisors and Operations & Admin roles.
                                        </p>
                                    </div>

                                    <div className="space-y-3">
                                        <Label>Please verify you are not a robot</Label>
                                        <ReCAPTCHA
                                            ref={recaptchaRef}
                                            sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || ''}
                                            onChange={(token) => setRecaptchaToken(token || '')}
                                        />
                                    </div>
                                </div>

                                <DialogFooter>
                                    <Button type="button" variant="outline" onClick={requestClose}>
                                        Cancel
                                    </Button>
                                    <Button type="submit" className="btn-accent" disabled={isSubmitting}>
                                        {isSubmitting ? 'Submitting...' : 'Submit Application'}
                                    </Button>
                                </DialogFooter>
                            </form>
                        )}
                    </div>
                </DialogContent>
            </Dialog>

            <AlertDialog open={showRoleConfirm} onOpenChange={setShowRoleConfirm}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Discard current application?</AlertDialogTitle>
                        <AlertDialogDescription>
                            You were applying for "{selectedRole}". Do you want to discard that and apply for "{pendingRole}"?
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel onClick={handleCancelRoleChange}>Continue current</AlertDialogCancel>
                        <AlertDialogAction onClick={handleConfirmRoleChange}>Discard & switch</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};

export default CareersPage;
